import React, { BaseSyntheticEvent, useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Box,
  CircularProgress,
  TextField,
} from '@mui/material';

import moment from 'moment';

import { useAppDispatch, useAppSelector } from '@redux/stores/hooks';
import { deleteHelpAction, fetchHelpsAction } from '@redux/actions/help';
import { RootState } from '@redux/reducers';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Help } from '@components/types/help/types';
import { Link } from 'react-router-dom';
import { ROUTES } from '@components/consts';

interface Column {
  id: 'order' | 'title' | 'content' | 'createdAt' | 'updatedAt' | 'actions';
  label: string;
  width?: string;
  align?: 'right' | 'center';
}

const columns: Column[] = [
  { id: 'order', label: 'Prio', width: '3%' },
  { id: 'title', label: 'Tytuł', width: '16.6666666667%' },
  { id: 'createdAt', label: 'Stworzony', width: '16.6666666667%' },
  { id: 'updatedAt', label: 'Zaktualizowany', width: '16.6666666667%' },
  { id: 'actions', label: 'Akcje', width: '10%', align: 'center' },
];

export const ManageHelp: React.FC = () => {
  const dispatch = useAppDispatch();
  const { list, loading } = useAppSelector((state: RootState) => state.help);

  moment.locale('pl');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState<string>('');

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const removeItem = (id: string) => {
    dispatch(deleteHelpAction(id));
  };

  const handleSearchChange = (event: BaseSyntheticEvent) => {
    setSearch(event.target.value);
  };

  useEffect(() => {
    dispatch(fetchHelpsAction());
  }, [dispatch]);

  const filteredAccordionData = list
    .filter((accordion) =>
      accordion.title.toLowerCase().includes(search.toLowerCase()) ||
      accordion.content.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (a.order < b.order) return 1;
      if (a.order > b.order) return -1;
      return a.title.localeCompare(b.title);
    });

  return (
    <>
      {loading ?
        (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={70} />
          </Box>
        ) : (
          <Paper sx={{
            width: '100%',
            padding: '10px',
            '& hr': {
              margin: '10px auto',
            },
          }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              sx={{
                padding: '10px',
              }}
            >
              <Typography
                component="h1"
                variant="h6"
                color="inherit"
                noWrap
                className="title"
              >
                Edycja Pomocy
              </Typography>
              <Button to={ROUTES.helpCreate.href} component={Link}>
                Dodaj element
              </Button>
            </Stack>
            <TextField
              label="Wyszukaj"
              type="search"
              sx={{ mt: 2, mb: 2 }}
              fullWidth
              value={search}
              onChange={handleSearchChange}
            />
            <TableContainer sx={{
              paddingTop: '20px',
              paddingBottom: '20px',
            }}>
              <Table size="small" stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        style={{ width: column.width }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAccordionData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item, index) => (
                      <ItemTableRow
                        key={item._id}
                        item={item}
                        index={index}
                        removeItem={removeItem}
                      />
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={list.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        )}
    </>
  );
};

interface ItemTableRowType {
  item: Help;
  index: number;
  removeItem: (id: string) => void;
}

const ItemTableRow: React.FC<ItemTableRowType> = ({
  item,
  removeItem,
}) => {
  const RemoveButton: React.FC<{ selectedItem: Help }> = ({
    selectedItem,
  }) => (
    <Tooltip title="Usuń">
      <IconButton
        sx={{
          '&:hover, &.Mui-focusVisible': {
            backgroundColor: 'rgba(245, 0, 87, 0.20)',
          },
        }}
        aria-label="Delete"
        color="error"
        onClick={() => removeItem(selectedItem._id!)}
      >
        <DeleteIcon />
      </IconButton>
    </Tooltip>
  );

  const EditButton: React.FC = () => (
    <Tooltip title="Edytuj">
      <IconButton
        sx={{
          '&:hover, &.Mui-focusVisible': {
            backgroundColor: 'rgba(63, 81, 181, 0.20)',
          },
        }}
        aria-label="Edit"
        color="info"
        component={Link}
        to={`${ROUTES.helpUpdate.preHref}/${item._id}`}
      >
        <EditIcon />
      </IconButton>
    </Tooltip>
  );
        console.log(columns);
  return (
    <TableRow hover role="checkbox" tabIndex={-1} key={item._id}>
      {columns.map((column) => (
        <TableCell key={column.id} align={column.align}>
          {column.id === 'actions' ? (
            <>
              <EditButton />
              <RemoveButton selectedItem={item} />
            </>
          ) : column.id === 'createdAt' || column.id === 'updatedAt' ? (
            item[column.id] ? moment(item[column.id]).format('DD.MM.YYYY HH:mm') : ''
          ) : column.id === 'order' ? (
            item[column.id]
          ) : (
            typeof item[column.id] === 'string' ? item[column.id].split(';base64,')[0] : ''
          )}
        </TableCell>
      ))}
    </TableRow>
  );
};
